import { useState, useEffect } from "react";
import { Button, Select, SelectOption, Textarea, Modal, Title } from "rizzui";
import { FaWhatsapp } from "react-icons/fa6";
import { PiXBold } from "react-icons/pi";
import OptionItemLayout from "@/app/app-shared/homepage-layout/forms/OptionItemLayout";
import { COUNTRY_SG_CODE } from "@/config/constants";
import {
  IEnquiryEntry,
  EActionTrackingType,
} from "@/utilities/types/EnquiryEntry";
import { createWhatsappMessage } from "@/utils/string";
import { openContactWhatsApp } from "@/utilities/functions/common";
import useApi from "@/hooks/useApi";
import MessageRepository from "@/utilities/repositories/Message";
import useProfile from "@/hooks/use-profile";
import { Roles } from "@/utilities/types/Users";
import dayjs from "dayjs";
import { LogActionTableRef } from "../table/logs-action-table";
import { Z_MODAL } from "@/config/zModal";
import EnquiryEntriesRepository from "@/utilities/repositories/EnquiryEntries";

interface TSelectMessage {
  label: string;
  value: string;
  content: string;
  isAdmin: boolean;
}

interface TSelectOptionMessage {
  label: string;
  value: string;
}

interface ContactModalProps {
  isOpen: boolean;
  onClose: () => void;
  data?: IEnquiryEntry;
  tableLogActionRef?: React.RefObject<LogActionTableRef>;
}

export default function ContactModal({
  isOpen,
  onClose,
  data,
  tableLogActionRef,
}: ContactModalProps) {
  const { profile } = useProfile();
  const [messageSelected, setMessageSelected] = useState<TSelectMessage>();
  const [valueMessage, setValueMessage] = useState<string>();
  const [whatsappMessage, setWhatsappMessage] = useState<string>("");
  const [optionsMessage, setOptionsMessage] = useState<TSelectOptionMessage[]>(
    [],
  );

  const { request: getAllMessageByAgency, response: messageByAgencyResponse } =
    useApi({
      request: MessageRepository.getMessagesByAgency,
    });

  const { request: getAllMessagesByAdmin, response: messageByAdminResponse } =
    useApi({
      request: MessageRepository.getMessagesByAdmin,
    });
  // Format phone number
  let convertPhone = data?.phone;
  const subString = " ";
  if (convertPhone) {
    !convertPhone.includes(COUNTRY_SG_CODE) &&
      (convertPhone = COUNTRY_SG_CODE + convertPhone);
    convertPhone =
      convertPhone.substring(0, 3) + subString + convertPhone.substring(3);
    convertPhone =
      convertPhone.substring(0, 8) + subString + convertPhone.substring(8);
  } else {
    convertPhone = "N/A";
  }

  useEffect(() => {
    if (profile?.role === Roles.ADMIN) {
      if (typeof getAllMessagesByAdmin === "function") {
        getAllMessagesByAdmin({});
      }
    } else {
      if (typeof getAllMessageByAgency === "function") {
        getAllMessageByAgency({});
      }
    }
  }, [getAllMessageByAgency, getAllMessagesByAdmin, profile?.role]);

  useEffect(() => {
    const temp =
      profile?.role === Roles.ADMIN
        ? messageByAdminResponse?.data?.items
        : messageByAgencyResponse?.data?.items;
    const Options = (Array.isArray(temp) ? temp : [])?.map((item) => ({
      label: item.subject,
      value: item.id,
      content: item.content,
      isAdmin: item.isAdmin,
    }));
    setMessageSelected(Options?.[0]);
    setValueMessage(Options?.[0]?.value || "");
    setOptionsMessage(Options);
  }, [
    messageByAdminResponse?.data?.items,
    messageByAgencyResponse?.data?.items,
    profile?.role,
  ]);

  useEffect(() => {
    if (optionsMessage) {
      const findOption: TSelectOptionMessage | undefined = optionsMessage.find(
        (item) => item.value === valueMessage,
      );
      // @ts-ignore
      setMessageSelected(findOption);
    }
  }, [optionsMessage, valueMessage]);

  useEffect(() => {
    const parsedMessage = createWhatsappMessage(
      messageSelected?.content || "",
      {
        "Recipient's Name": data?.name || "",
        customer_name: data?.name || "",
        customer_email: data?.email || "",
        customer_phone: data?.phone || "",
        agent_name: data?.assignedTo
          ? `${data?.assignedTo.firstName ? `${data?.assignedTo.firstName} ` : ""}${data?.assignedTo.lastName || ""}`
          : `${data?.user?.firstName ? `${data?.user?.firstName} ` : ""}${data?.user?.lastName || ""}`,
        agent_email: data?.assignedTo?.email || data?.user?.email || "",
        project_name:
          typeof data?.project?.name === "object"
            ? data?.project?.name?.en
            : data?.project?.name || "",
        project_address:
          typeof data?.project?.address === "object"
            ? data?.project?.address?.en
            : data?.project?.address || "",
        project_area: data?.project?.area || "",
        project_tenure: data?.project?.tenure || "",
        project_tenure_effect_from:
          data?.project?.tenureEffectFrom &&
          dayjs(data?.project?.tenureEffectFrom).isValid()
            ? dayjs(data?.project?.tenureEffectFrom).format("DD MMM YYYY")
            : "",
        appointment_date_time: dayjs(data?.appointment).isValid()
          ? dayjs(data?.appointment).format("MMMM DD, YYYY hh:mm A")
          : "Not Given",
      },
    );

    setWhatsappMessage(parsedMessage);
  }, [messageSelected?.content, data]);

  const handleOpenWhatsapp = async () => {
    try {
      openContactWhatsApp(
        data?.phone?.startsWith("0")
          ? `+65${data?.phone?.slice(1) || ""}`
          : data?.phone || "",
        whatsappMessage,
      );
      await EnquiryEntriesRepository.createLogAction({
        submissionId: data?.id || "",
        trackingType: EActionTrackingType.CLICK_CONTACT_WHATSAPP_BUTTON,
      });
      await tableLogActionRef?.current?.refresh();
    } catch (error) {
      console.log(error);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      size="lg"
      className={Z_MODAL.CONTACT}
    >
      <div className="p-6">
        <div className="flex items-center justify-between mb-6">
          <Title as="h3" className="text-xl font-semibold">
            Contact Customer
          </Title>
          <button
            onClick={onClose}
            className="p-1 hover:bg-gray-100 rounded-full transition-colors"
          >
            <PiXBold className="h-5 w-5 text-gray-500" />
          </button>
        </div>

        <div className="space-y-4">
          {/* Message Template Selection */}
          {optionsMessage.length > 0 && (
            <Select
              options={optionsMessage}
              value={valueMessage}
              onChange={setValueMessage}
              label="Message Template"
              className="mb-3"
              getOptionValue={(option: SelectOption) => option.value}
              displayValue={(selected: string) => {
                const findOption: TSelectOptionMessage | undefined =
                  optionsMessage?.find((option) => option.value === selected);
                // @ts-ignore
                setMessageSelected(findOption);
                // @ts-ignore
                return findOption?.label;
              }}
              getOptionDisplayValue={(option) => {
                return <OptionItemLayout option={option} />;
              }}
              dropdownClassName="z-modal [&_li>div]:w-full"
            />
          )}

          {/* Message Content */}
          <div>
            <Textarea
              label="Message Content"
              placeholder="Content Message"
              value={whatsappMessage}
              onChange={(e) => setWhatsappMessage(e.target.value)}
              rows={6}
            />
          </div>

          {/* Action Buttons */}
          <div className="flex flex-col items-center justify-center gap-3 pt-4">
            <Button className="w-full h-fit" onClick={handleOpenWhatsapp}>
              <span className="flex gap-2 items-center">
                <FaWhatsapp /> Contact {convertPhone} on WhatsApp
              </span>
            </Button>
            <Button
              variant="outline"
              className="w-full h-fit"
              onClick={onClose}
            >
              Cancel
            </Button>
          </div>
        </div>
      </div>
    </Modal>
  );
}
